export interface CertificateField {
  label: string;
  value: string;
}

export interface CertificatePreview {
  kind: 'certificate' | 'csr' | 'private-key' | 'unknown';
  fields: CertificateField[];
}

export function parseCertificate(bytes: Uint8Array, fileName = ''): CertificatePreview {
  const text = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  if (text.includes('-----BEGIN')) {
    return parsePem(text);
  }
  return parseDer(bytes, fileName);
}

function parsePem(text: string): CertificatePreview {
  const blocks = [...text.matchAll(/-----BEGIN ([^-]+)-----([\s\S]*?)-----END \1-----/g)];
  if (blocks.length === 0) {
    return { kind: 'unknown', fields: [{ label: 'format', value: 'PEM (unrecognized)' }] };
  }

  const [first] = blocks;
  const label = first[1].trim();
  if (label.includes('PRIVATE KEY')) {
    return { kind: 'private-key', fields: [{ label: 'type', value: label }] };
  }
  const der = pemToDer(first[2]);
  if (label.includes('CERTIFICATE REQUEST')) return parseCertificateRequest(der);
  if (label.includes('CERTIFICATE')) return parseX509(der);
  return { kind: 'unknown', fields: [{ label: 'type', value: label }] };
}

function parseDer(bytes: Uint8Array, fileName: string): CertificatePreview {
  if (bytes[0] !== 0x30) {
    return { kind: 'unknown', fields: [{ label: 'format', value: 'binary' }] };
  }
  if (fileName.toLowerCase().endsWith('.csr')) return parseCertificateRequest(bytes);
  return parseX509(bytes);
}

function pemToDer(body: string): Uint8Array {
  const cleaned = body.replace(/\s+/g, '');
  const binary = atob(cleaned);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function parseX509(bytes: Uint8Array): CertificatePreview {
  try {
    const root = readNode(bytes, 0);
    const tbs = root.children[0];
    let idx = 0;
    if (tbs.children[idx]?.tag === 0xa0) idx += 1;
    const serial = formatInteger(tbs.children[idx++]);
    idx += 1; // signature algorithm
    const issuer = formatName(tbs.children[idx++]);
    const validity = tbs.children[idx++];
    const notBefore = formatTime(validity.children[0]);
    const notAfter = formatTime(validity.children[1]);
    const subject = formatName(tbs.children[idx++]);
    idx += 1; // subject public key
    const extensions = tbs.children[idx]?.tag === 0xa3
      ? readExtensions(tbs.children[idx].children[0])
      : [];

    return {
      kind: 'certificate',
      fields: [
        { label: 'type', value: 'X.509 certificate' },
        { label: 'serial', value: serial },
        { label: 'subject', value: subject },
        { label: 'issuer', value: issuer },
        { label: 'validFrom', value: notBefore },
        { label: 'validTo', value: notAfter },
        ...extensions,
      ],
    };
  } catch {
    return { kind: 'certificate', fields: [{ label: 'type', value: 'X.509 certificate (parse error)' }] };
  }
}

function parseCertificateRequest(bytes: Uint8Array): CertificatePreview {
  try {
    const root = readNode(bytes, 0);
    const info = root.children[0];
    const version = readInteger(info.children[0]);
    const subject = formatName(info.children[1]);
    return {
      kind: 'csr',
      fields: [
        { label: 'type', value: 'Certificate signing request' },
        { label: 'version', value: String(version) },
        { label: 'subject', value: subject },
      ],
    };
  } catch {
    return { kind: 'csr', fields: [{ label: 'type', value: 'CSR (parse error)' }] };
  }
}

function readExtensions(node: Asn1Node): CertificateField[] {
  const fields: CertificateField[] = [];
  for (const ext of node.children) {
    const oid = decodeOid(ext.children[0].bytes);
    let valueIndex = 1;
    if (ext.children[valueIndex]?.tag === 0x01) valueIndex += 1;
    const value = ext.children[valueIndex];
    if (oid === '2.5.29.17') {
      fields.push({ label: 'subjectAltName', value: parseSan(value.bytes) });
    } else if (oid === '2.5.29.15') {
      fields.push({ label: 'keyUsage', value: parseKeyUsage(value.bytes) });
    }
  }
  return fields;
}

function parseSan(bytes: Uint8Array): string {
  const node = readNode(bytes, 0);
  return node.children
    .map((child) => new TextDecoder().decode(child.bytes))
    .join(', ');
}

function parseKeyUsage(bytes: Uint8Array): string {
  const node = readNode(bytes, 0);
  const bits = node.bytes[node.bytes.length - 1] ?? 0;
  const usages = [
    'digitalSignature',
    'nonRepudiation',
    'keyEncipherment',
    'dataEncipherment',
    'keyAgreement',
    'keyCertSign',
    'cRLSign',
    'encipherOnly',
    'decipherOnly',
  ];
  return usages.filter((_, i) => (bits & (1 << (7 - i))) !== 0).join(', ');
}

function formatName(node: Asn1Node): string {
  const parts: string[] = [];
  for (const set of node.children) {
    for (const pair of set.children) {
      const oid = decodeOid(pair.children[0].bytes);
      const value = new TextDecoder().decode(pair.children[1].bytes);
      parts.push(`${oidToShortName(oid)}=${value}`);
    }
  }
  return parts.join(', ');
}

function oidToShortName(oid: string): string {
  const map: Record<string, string> = {
    '2.5.4.3': 'CN',
    '2.5.4.6': 'C',
    '2.5.4.7': 'L',
    '2.5.4.8': 'ST',
    '2.5.4.10': 'O',
    '2.5.4.11': 'OU',
  };
  return map[oid] ?? oid;
}

function formatTime(node: Asn1Node): string {
  const text = new TextDecoder().decode(node.bytes);
  if (node.tag === 0x17 && text.length === 13) {
    return `${text.slice(0, 2)}-${text.slice(2, 4)}-${text.slice(4, 6)} ${text.slice(6, 8)}:${text.slice(8, 10)}:${text.slice(10, 12)} UTC`;
  }
  if (node.tag === 0x18 && text.length >= 14) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}:${text.slice(12, 14)} UTC`;
  }
  return text;
}

function formatInteger(node: Asn1Node): string {
  return [...node.bytes].map((b) => b.toString(16).padStart(2, '0')).join(':');
}

function readInteger(node: Asn1Node): number {
  let n = 0;
  for (const b of node.bytes) n = (n << 8) | b;
  return n;
}

interface Asn1Node {
  tag: number;
  bytes: Uint8Array;
  children: Asn1Node[];
  end: number;
}

function readNode(bytes: Uint8Array, offset: number): Asn1Node {
  const tag = bytes[offset];
  const { length, headerSize } = readLength(bytes, offset + 1);
  const start = offset + 1 + headerSize;
  const end = start + length;
  const content = bytes.subarray(start, end);
  const constructed = (tag & 0x20) !== 0;
  const children: Asn1Node[] = [];
  if (constructed) {
    let childOffset = 0;
    while (childOffset < content.length) {
      const child = readNode(content, childOffset);
      children.push(child);
      childOffset = child.end;
    }
  }
  return { tag, bytes: content, children, end };
}

function readLength(bytes: Uint8Array, offset: number): { length: number; headerSize: number } {
  const first = bytes[offset];
  if (first < 0x80) return { length: first, headerSize: 1 };
  const count = first & 0x7f;
  let length = 0;
  for (let i = 0; i < count; i++) length = (length << 8) | bytes[offset + 1 + i];
  return { length, headerSize: 1 + count };
}

function decodeOid(bytes: Uint8Array): string {
  if (bytes.length === 0) return '';
  const first = bytes[0];
  const parts = [Math.floor(first / 40), first % 40];
  let value = 0;
  for (let i = 1; i < bytes.length; i++) {
    const b = bytes[i];
    value = (value << 7) | (b & 0x7f);
    if ((b & 0x80) === 0) {
      parts.push(value);
      value = 0;
    }
  }
  return parts.join('.');
}
